import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid, SvgIcon, Typography, Collapse, Button, Box } from "@material-ui/core";
import Table from "../../components/Layout/Table";
import useColumnSelection from "../../hooks/useColumnSelection";
import uuidFromUrl from "../../util/strings/uuidFromUrl";
import { FleetContext, VehicleContext } from "../../context";
import { SectionGridContainer, SectionGridLayout } from "../../components/Layout";
import { CellReceptionDisplay } from "../../components/Icons/CellReceptionDisplay";
import { PushPin } from "../../components/Icons/PushPin";
import { Search } from "../../components/FormElements";
import { StringUtils } from "../../util";

const useStyles = makeStyles((theme) => ({
  filtersWrapper: {
    opacity: 1,
    transition: "0.8s ease-in",
    zIndex: 1,
  },
  filtersWrapperHidden: {
    marginTop: "-20px",
    opacity: 0,
    transition: "0.8s ease-out",
    zIndex: -1,
  },
  shortCodeCell: {
    padding: 0,
    border: 0,
  },
  fleetCell: {
    border: 0,
    padding: "0 8px",
    textTransform: "none",
  },
  iconMargin: {
    marginRight: theme.spacing(1),
  },
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const Vehicle = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const history = useHistory();
  const { fleets, setActiveFleet, setActiveVehicle } = useContext(FleetContext);
  const {
    vehicles,
    vehicleCount,
    vehicleSearchParams,
    initialSearchParams,
    searchVehicles,
    setVehicleSearchParams,
  } = useContext(VehicleContext);

  const [displayableVehicles, setDisplayableVehicles] = useState([]);
  const [fleetNameForId, setFleetNameForId] = useState({});
  const [pinnedRows, setPinnedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleNavigateTo = (btn, row) => {
    const fleetId = uuidFromUrl(row.fleets[0]);
    let url = `/fleets/${fleetId}`;
    if (btn === "vehicle") {
      setActiveVehicle(row);
      url += `/vehicles/${row.id}`;
    }
    setActiveFleet(fleetId);
    history.push(url);
    setVehicleSearchParams(initialSearchParams);
  };

  const handlePin = (vehicle) => {
    const { id } = vehicle;
    if (id in pinnedRows) {
      const remainingVehicles = displayableVehicles.filter((v) => v.id !== id);
      const remainingPins = { ...pinnedRows };
      delete remainingPins[id];
      setDisplayableVehicles(remainingVehicles);
      setPinnedRows(remainingPins);
    } else {
      const sortedByPinned = [
        vehicle,
        ...Object.values(pinnedRows),
        ...vehicles.filter((v) => !(v.id === id || v.id in pinnedRows)),
      ];
      setDisplayableVehicles(sortedByPinned);
      setPinnedRows({
        ...pinnedRows,
        [id]: vehicle,
      });
    }
  };

  const tableColumns = [
    {
      canonicalName: "shortcodes__shortcode",
      name: "Vehicles",
      sortable: true,
      renderCellContent: (row) => {
        const [{ shortcode: shortCode }] = row.shortcodes;
        const fleetUrl = row.fleets[0];

        const fleetId = fleetUrl ? uuidFromUrl(fleetUrl) : fleets[0].uuid4;
        const vehicleId = row.id;
        const url = `/fleets/${fleetId}/vehicles/${vehicleId}`;
        return (
          <Button
            className={classes.shortCodeCell}
            component={RouterLink}
            onClick={fleetUrl && (() => handleNavigateTo("vehicle", row))}
            to={fleetUrl && url}
          >
            {shortCode}
          </Button>
        );
      },
    },
    {
      canonicalName: "fleets__name",
      name: "Fleets",
      sortable: true,
      renderCellContent: (row) => {
        if (row.fleets[0]) {

          const fleetId = uuidFromUrl(row.fleets[0]);
          const fleet = fleetNameForId[fleetId];
          const url = `/fleets/${fleetId}`;
          return (
            <Button
              className={classes.fleetCell}
              component={RouterLink}
              onClick={row.fleets[0] && (() => handleNavigateTo("fleet", row))}
              to={url}
              disabled={!row.fleets[0]}
            >
              {fleet}
            </Button>
          );
        }

        return null;
      },
    },
    {
      canonicalName: "target_state",
      name: "Target State(s)",
      sortable: true,
      renderCellContent: (row) => (
        <Box style={{ display: "flex", padding: 0, margin: 0 }}>
          <SvgIcon
            fontSize="small"
            htmlColor={
              theme.palette.statusColors[row.has_state_mismatch ? "mismatched" : row.target_state]
            }
            style={{
              marginRight: theme.spacing(1),
            }}
          >
            <circle cx="9" cy="13" r="9" />
          </SvgIcon>
          <Typography>{t(`Vehicles.Search.Statuses.${row.target_state}`)}</Typography>
        </Box>
      ),
    },
    {
      canonicalName: "last_reported_state",
      name: "Last Reported",
      sortable: true,
      renderCellContent: (row) => (
        <Typography>
          {row.last_reported_state && t(`Vehicles.Search.Statuses.${row.last_reported_state}`)}
        </Typography>
      ),
    },
    {
      canonicalName: "last_ingest",
      name: "Last Ingest",
      sortable: true,
      renderCellContent: (row) => (
        <Typography>
          {row.last_ingest &&
            `${StringUtils.convertTz(row.last_ingest, "UTC", null, "DATETIME_FULL")}`}
        </Typography>
      ),
    },
    {
      canonicalName: "cellular_network",
      name: "Cellular Network",
      renderCellContent: (row) => (
        <Typography>{row.last_reported_network && row.last_reported_network}</Typography>
      ),
    },
    {
      canonicalName: "cell_bars",
      name: "Cellular",
      renderCellContent: (row) => (
        <CellReceptionDisplay percentage={row.cell_bars * 25} width={24} height={16} />
      ),
    },
    {
      canonicalName: "pinned",
      name: "Pinned",
      renderCellContent: (row) => (
        <PushPin onPin={() => handlePin(row)} pinned={row.id in pinnedRows} />
      ),
    },
  ];

  const { onSelectColumn, sortBy, sortDir } = useColumnSelection({
    columns: tableColumns.filter(({ sortable }) => sortable).map((col) => col.canonicalName),
    sortBy: "",
    sortDir: "asc",
  });

  useEffect(() => {
    if (fleets.length) {
      const idForName = {};
      fleets.forEach((fleet) => {
        idForName[fleet.uuid4] = fleet.name;
      });
      setFleetNameForId(idForName);
    }
  }, [fleets, vehicles]);

  useEffect(() => {
    setDisplayableVehicles([
      ...Object.values(pinnedRows),
      ...vehicles.filter((v) => !(v.id in pinnedRows)),
    ]);
  }, [vehicles, pinnedRows]);

  useEffect(() => {
    const waitForVehicles = async () => {
      setLoading(true);
      await searchVehicles(
        vehicleSearchParams,
        sortBy && (sortDir === "asc" ? sortBy : `-${sortBy}`),
        rowsPerPage,
        pageNumber + 1,
      );
      setLoading(false);
    };
    if (vehicleSearchParams.search) {
      waitForVehicles();
    } else {
      searchVehicles(initialSearchParams);
    }
  }, [vehicleSearchParams, pageNumber, rowsPerPage, sortBy, sortDir]);

  return (
    <SectionGridLayout>
      <SectionGridContainer spaceContents>
        <Grid item xs={12}>
          <Typography variant="h5">Find Vehicle</Typography>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} md={4}>
            <Search
              onChange={(text) => setVehicleSearchParams({ search: text })}
              debounce={500}
              placeholder={t("Vehicles.Search.Placeholder")}
              loading={loading}
            />
          </Grid>
        </Grid>
      </SectionGridContainer>
      <Collapse in={displayableVehicles.length > 0} timeout={1000}>
        <SectionGridContainer style={{ maxHeight: "100%" }}>
          <Table
            rows={displayableVehicles}
            columns={tableColumns}
            paginationProps={{
              rowsPerPage,
              rowsPerPageOptions: [5, 10, 50, 100],
              pageNumber,
              totalRowsCount: vehicleCount + Object.keys(pinnedRows).length,
              setPageNumber,
              setRowsPerPage,
            }}
            sortProps={{
              sortBy,
              sortDir,
              onSelectColumn,
            }}
            pinnedRows={pinnedRows}
          />
        </SectionGridContainer>
      </Collapse>
    </SectionGridLayout>
  );
};

export default Vehicle;
